import { FALLBACK_YEAR } from './newApi';

export const currency = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
};

export const percent = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  return Number(n).toFixed(1) + '%';
};

// Parse CSV helper function
function parseCSV(text) {
  const lines = text.split('\n');
  if (!lines.length) return [];
  
  const headers = parseCSVLine(lines[0]).map(h => h.replace(/^"|"$/g, '').trim());
  const data = [];
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const values = parseCSVLine(line);
    const row = {};
    
    headers.forEach((header, index) => {
      let value = values[index]?.replace(/^"|"$/g, '').trim() || '';
      
      // Convert numeric strings to numbers
      if (value && !isNaN(value)) {
        value = Number(value);
      }
      
      row[header] = value;
    });
    
    data.push(row);
  }
  
  return data;
}

// Helper function to parse CSV line respecting quoted fields
function parseCSVLine(line) {
  const values = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      values.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  values.push(current.trim());
  return values;
}

// Load and parse transplant centers CSV
let centersData = null;

export async function loadTransplantCenters() {
  if (centersData) return centersData;
  
  try {
    const response = await fetch('/Transplant Centers-IOTA.csv');
    const text = await response.text();
    
    // Parse CSV
    const lines = text.split('\n');
    const headers = parseCSVLine(lines[0]).map(h => h.replace(/^"|"$/g, '').trim());
    const nameIndex = headers.findIndex(h => h === 'Name');
    const ctrCdIndex = headers.findIndex(h => h === 'Center Code');
    
    centersData = {};
    
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      const values = parseCSVLine(line);
      const ctrCd = values[ctrCdIndex]?.replace(/^"|"$/g, '').trim();
      const name = values[nameIndex]?.replace(/^"|"$/g, '').trim();
      
      if (ctrCd && name) {
        centersData[ctrCd.toUpperCase()] = name;
      }
    }
    
    return centersData;
  } catch (error) {
    console.error('Error loading transplant centers:', error);
    return {};
  }
}

export function getCenterNameById(centerId) {
  if (!centersData || !centerId) return null;
  return centersData[centerId.toUpperCase()] || null;
}

export function getAllCenters() {
  if (!centersData) return [];
  
  // Return array of objects with id and name
  return Object.entries(centersData)
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export function getCenterIdByName(centerName) {
  if (!centersData || !centerName) return null;
  
  const entry = Object.entries(centersData).find(([id, name]) => 
    name.toLowerCase() === centerName.toLowerCase()
  );
  
  return entry ? entry[0] : null;
}

// Load and parse merged summary data
let summaryData = null;

export async function loadSummaryData() {
  if (summaryData) return summaryData;
  
  try {
    const response = await fetch('/Transplant Centers-IOTA.csv');
    const text = await response.text();
    summaryData = parseCSV(text);
    return summaryData;
  } catch (error) {
    console.error('Error loading summary data:', error);
    return [];
  }
}

export function getSummaryData() {
  return summaryData || [];
}

export function getCenterPerformanceY1(centerId) {
  if (!summaryData || !centerId) return null;
  
  const centerCode = centerId.toUpperCase();
  const centerRow = summaryData.find(row => row['Center Code'] === centerCode);
  
  if (!centerRow) return null;
  
  // Use FALLBACK_YEAR to get the most recent actual data
  const totalTransplants = centerRow[`${FALLBACK_YEAR} - Total Transplants`] || null;
  const livingTransplants = centerRow[`${FALLBACK_YEAR} - Living Transplants`] || null;
  
  // Calculate living donor percentage
  let livingDonorPercent = null;
  if (totalTransplants && livingTransplants && totalTransplants > 0) {
    livingDonorPercent = (livingTransplants / totalTransplants) * 100;
  }
  
  return {
    numTransplants: totalTransplants,
    offerAcceptRate: centerRow[`${FALLBACK_YEAR} - Organ offer acceptance rate`] || null,
    graftSurvival: centerRow[`${FALLBACK_YEAR} - Graft survival rate`] || null,
    livingDonorPercent: livingDonorPercent,
    isIOTA: centerRow['IOTA'] === 1
  };
}

export function isIOTACenter(centerId) {
  if (!summaryData || !centerId) return false;
  
  const centerCode = centerId.toUpperCase();
  const centerRow = summaryData.find(row => row['Center Code'] === centerCode);
  
  return centerRow && centerRow['IOTA'] === 1;
}

export function centerExists(centerId) {
  if (!summaryData || !centerId) return false;
  
  const centerCode = centerId.toUpperCase();
  const centerRow = summaryData.find(row => row['Center Code'] === centerCode);
  
  return !!centerRow;
}
